import type { AccountInfo } from '@azure/msal-browser';
import type { JwtToken } from '~/utils/types';
import { useCustomerAccess } from '~/composables/useCustomerAccess';

export const useApiAccessToken = () =>
  useState<{ accessToken: string; accessTokenDecoded: JwtToken } | undefined>(
    'apiAccessToken',
    () => undefined
  );

const amvTenantId = '0bba774b-8346-43b4-b237-ea91afa02929';
const softwareDeveloperTenantIds = ['f534904a-7f72-4fee-aefe-3d7263612525', 'eb2a706c-9d49-405f-bafb-afa7f98076dc']; // Webstep, Ring

export const useAuth = () => {
  const { $msal } = useNuxtApp();
  const config = useRuntimeConfig();
  const authUser = useState<AccountInfo | null>('user', () => null);
  const isAuthenticated = useState<boolean>('isAuthenticated', () => false);
  const adminTenantIds = useState<string[]>('adminTenantIds', () => config.public.adminTenantIds);
  const hasValidTenant = useState<boolean>('hasValidTenant', () => true);
  const customerRoles = useState<string | null>('customerRoles', () => null);



  const setUser = (user: AccountInfo | null) => {
    if (user) {
      authUser.value =
        config.public.appEnv === 'prod'
          ? user
          : {
              ...user,
              tenantId:
              softwareDeveloperTenantIds.includes(user.tenantId)
                  ? amvTenantId
                  : user.tenantId,
            };
      isAuthenticated.value = true;
    } else {
      authUser.value = null;
      isAuthenticated.value = false;
    }
  };

  watch(authUser, async (user) => {
    if (user) {
      const { data, error } = await useFetch<boolean>(`/api/tenants/${user.tenantId}/valid`);
      if (error.value) {
        hasValidTenant.value = false;
        navigateTo('/forbidden');
      } else {
        hasValidTenant.value = data.value === true;
      }

      // Fetch customer roles from DB
      const token = useApiAccessToken().value?.accessToken;
      if (token) {
        try {
          const response = await $fetch<any>(`${config.public.gatewayUrl}/graphql`, {
            method: 'POST',
            headers: { Authorization: `Bearer ${token}` },
            body: {
              query: `
                query GetMyCustomerRoles {
                  customers {
                    nodes {
                      roles
                    }
                  }
                }
              `
            }
          });
          const rolesArr: string[] = response?.data?.customers?.nodes?.[0]?.roles ?? [];
          customerRoles.value = rolesArr.length > 0 ? rolesArr.join(',') : null;
        } catch (e) {
          console.error('Failed to fetch roles', e);
          customerRoles.value = null;
        }
      }
    } else {
      customerRoles.value = null;
    }
  });

  const isAdmin = computed(() => {
    const token = useApiAccessToken().value;
    return (
      !!token &&
      (token.accessTokenDecoded.roles?.includes('Admin') ?? false) &&
      (adminTenantIds.value.includes(token.accessTokenDecoded.tid ?? '') ||
        softwareDeveloperTenantIds.includes(token.accessTokenDecoded.tid ?? '') ||
        token.accessTokenDecoded.tid === amvTenantId)
    );
  });
  // Helper to get the decoded token safely
  const tokenDecoded = computed(() => useApiAccessToken().value?.accessTokenDecoded);

  const { 
    hasBasicAccess: hasBasic, 
    hasIntermediateAccess: hasIntermediate, 
    hasPremiumAccess: hasPremium 
  } = useCustomerAccess(customerRoles);

  const hasPremiumAccess = computed(() => {
    return isAdmin.value || hasPremium.value || (tokenDecoded.value?.roles?.includes('Premium.Access') ?? false);
  });

  const hasIntermediateAccess = computed(() => {
    return hasPremiumAccess.value || hasIntermediate.value || (tokenDecoded.value?.roles?.includes('Intermediate.Access') ?? false);
  });

  const hasBasicAccess = computed(() => {
    return hasIntermediateAccess.value || hasBasic.value || (tokenDecoded.value?.roles?.includes('Basic.Access') ?? false);
  });

  const login = async () => {
    try {
      const apiAccessScope = `${config.public.gatewayIdentifierUri}/api-access`;
      const loginRequest = {
        scopes: [
          'openid',
          'profile',
          'User.Read',
          apiAccessScope
        ]
      };
      await $msal().loginRedirect(loginRequest);
    } catch (error) {
      console.error('MSAL Login error: ', error);
    }
  };

  const logout = async () => {
    try {
      await $msal().logoutRedirect();
    } catch (error) {
      console.error('MSAL Logout error: ', error);
    } finally {
      setUser(null);
      $msal().setActiveAccount(null);
    }
  };

  return {
    login,
    logout,
    setUser,
    isAdmin,
    hasPremiumAccess,
    hasIntermediateAccess,
    hasBasicAccess,
    hasValidTenant,
    isAuthenticated,
    me: authUser
  };
};
